


import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams, useNavigationType } from 'react-router-dom';
import axios from 'axios';
import AuthPage from './components/AuthPage';
// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import PolicyPage from './components/PolicyPage';
import GDPRBanner from './components/GDPRBanner';
import RestaurantMenuApp from './components/RestaurantMenuApp';
import StoreSelector from './components/StoreSelector';
import StoreClosedModal from './components/StoreClosedModal';
import OrderTypeSelector from './components/OrderTypeSelector';
import LocationModal from './components/LocationModal';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import PaymentResult from './components/PaymentResult';
import CashOrderSuccess from './components/CashOrderSuccess';

// Profile Section Components
import { MyOrdersModal } from './components/MyOrdersModal';
import { MyTransactionsModal } from './components/MyTransactionsModal';
import { ManageAddressesModal } from './components/ManageAddressesModal';
import { PreferencesModal } from './components/PreferencesModal';
import { LoyaltyModal } from './components/LoyaltyModal';

// Import configurations
import { ComponentMap } from './config/ComponentRegistry';
import { brandingService } from './config/brandingService';
import { getTenantId } from './config/tenantConfig';
import { getValidAuthSession, clearAuthSession } from './utils/authStorage';
import { getRestaurantConfig, restaurantConfig } from './config/restaurantConfig';
import { getTemplate, getTemplateSync, generateCSSVariables } from './config/TemplateStyles';

// Import utilities
import { findNearestStore, calculateDistance } from './utils/storeUtils';
import { checkStoreAvailability, getStoreStatus } from './components/checkStoreAvailability';
import { storeNameToSlug } from './utils/urlStateManager';
import { apiCache } from './utils/apiCache';
import useAddressManager from './components/Useaddressmanager/useAddressManager';

// Import context
import { OrderFlowProvider, useOrderFlow } from './context/OrderFlowContext';

// Import styles - IMPORTANT: App.css must load AFTER bootstrap for overrides to work
import './css/bootstrap.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/font-awesome.min.css';
import './css/flaticon.css';
import './css/normalize.css';
import './css/bootstrap-select.min.css';
import './css/bootstrap-datetimepicker.css';
import './css/slick.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './css/responsive.css';
import './config/templates.css';
import './css/cafe-template.css';
import './App.css';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

// outletType is resolved dynamically from config/restaurantConfig


// ============================================
// API CLIENT - Singleton instance
// ============================================
const createApiClient = () => {
  const tenantId = getTenantId();
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Api-Key': X_API_KEY,
      'X-Tenant': tenantId
    }
  });
};

// Fuzzy match with caching
const fuzzyMatchCache = new Map();

const fuzzyMatchStore = (stores, targetSlug) => {
  const cacheKey = `${targetSlug}_${stores.length}`;
  const cached = fuzzyMatchCache.get(cacheKey);
  if (cached) return cached;

  // Try exact match first
  let match = stores.find(store => storeNameToSlug(store.name) === targetSlug);

  if (match) {
    fuzzyMatchCache.set(cacheKey, match);
    return match;
  }

  // Stricter fuzzy match (90% similarity)
  const targetParts = targetSlug.split('-').filter(Boolean);

  match = stores.find(store => {
    const storeSlug = storeNameToSlug(store.name);
    const storeParts = storeSlug.split('-').filter(Boolean);

    if (Math.abs(targetParts.length - storeParts.length) > 1) return false;

    const commonParts = targetParts.filter(part => storeParts.includes(part));
    return (commonParts.length / targetParts.length) >= 0.9;
  });

  if (match) {
    fuzzyMatchCache.set(cacheKey, match);
  }

  return match;
};

// Fetch store by slug
const fetchStoreByNameSlug = async (storeSlug) => {
  try {
    const apiClient = createApiClient();

    // Check cache first
    const cachedBranches = apiCache.getBranches();
    let stores = cachedBranches;

    if (!stores) {
      const response = await apiClient.get('/web/branch');
      stores = response.data.data;
      if (stores?.length) {
        apiCache.setBranches(stores);
      }
    }

    if (!stores?.length) return null;

    let matchedStore = fuzzyMatchStore(stores, storeSlug);

    // Fallback: Check if slug is in 'store-{id}' format
    if (!matchedStore && storeSlug.startsWith('store-')) {
      const storeId = parseInt(storeSlug.split('-')[1], 10);
      if (!isNaN(storeId)) {
        matchedStore = stores.find(s => parseInt(s.id, 10) === storeId);
      }
    }

    if (!matchedStore) return null;

    // Check detail cache (if cached data says open, use it; if closed, bypass cache to re-verify live)
    const cachedDetails = apiCache.getStoreDetails(matchedStore.id);
    if (cachedDetails?.week_schedule) {
      const availability = checkStoreAvailability(cachedDetails.week_schedule);
      if (availability.isOpen) {
        return cachedDetails;
      }
      // If cached data says closed, invalidate cache so fresh branch data is loaded below
      apiCache.invalidateStoreDetails(matchedStore.id);
    }

    // ✅ PERF: This used to always follow up with a GET /web/branch/{id}
    // call for details — but the /web/branch LIST response (fetched above)
    // already includes these same fields per branch, exactly as
    // StoreSelector already relies on (see its fetchStores(), which reads
    // week_schedule/allow_pre_order/pre_order_enabled straight off the list
    // item). That made every first-time slug resolution on a page reload pay
    // for a fully redundant second network round-trip before the menu could
    // even start loading — building storeData from the list match directly
    // instead removes it.
    const storeData = {
      id: matchedStore.id,
      name: matchedStore.name,
      location: matchedStore.location || `${matchedStore.city}, ${matchedStore.state}`,
      latitude: matchedStore.latitude,
      longitude: matchedStore.longitude,
      week_schedule: matchedStore.week_schedule,
      allow_pre_order: matchedStore.allow_pre_order || false,
      pre_order_enabled: matchedStore.pre_order_enabled || false,
      city: matchedStore.city,
      state: matchedStore.state
    };

    apiCache.setStoreDetails(matchedStore.id, storeData);
    return storeData;
  } catch (error) {
    console.error('❌ Error fetching store:', error.message);
    return null;
  }
};

// ============================================
// THEME PROVIDER
// ============================================
const ThemeProvider = ({ template, customTheme, backgroundImages, children }) => {
  // ✅ FIX: Load synchronously if possible to prevent FOUC (Flash of Unstyled Content)
  const [templateConfig, setTemplateConfig] = useState(() => {
    try {
      return getTemplateSync(template);
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(!templateConfig);

  useEffect(() => {
    if (templateConfig) {
      setLoading(false);
      return;
    }

    const loadTemplate = async () => {
      try {
        const brandedTemplate = await getTemplate(template);
        setTemplateConfig(brandedTemplate);
        setLoading(false);
      } catch (error) {
        console.error('❌ Error loading template:', error);
        setTemplateConfig(getTemplateSync(template));
        setLoading(false);
      }
    };
    loadTemplate();
  }, [template, templateConfig]);

  const mergedTheme = useMemo(() => {
    if (!templateConfig) return null;
    return {
      ...templateConfig,
      colors: {
        ...templateConfig.colors,
        ...(customTheme.primaryColor && { primary: customTheme.primaryColor }),
        ...(customTheme.secondaryColor && { secondary: customTheme.secondaryColor }),
        ...(customTheme.accentColor && { accent: customTheme.accentColor })
      }
    };
  }, [templateConfig, customTheme]);

  const cssVariables = useMemo(() => {
    if (!mergedTheme) return {};
    return {
      ...generateCSSVariables(mergedTheme),
      '--bg-banner': `url(${backgroundImages.banner})`,
      '--bg-newsletter': `url(${backgroundImages.newsletter})`,
      '--bg-footer': `url(${backgroundImages.footer})`,
      '--bg-special-menu': `url(${backgroundImages.specialMenu})`,
      '--bg-team': `url(${backgroundImages.team})`,
      '--bg-blog': `url(${backgroundImages.blog})`
    };
  }, [mergedTheme, backgroundImages]);

  const templateClass = templateConfig ? `template-${templateConfig.id}` : '';

  useEffect(() => {
    if (!templateConfig) return;
    const linkId = 'google-fonts-link';
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?${templateConfig.fonts.googleFonts}&display=swap`;
  }, [templateConfig?.fonts?.googleFonts]);

  // Prevent FOUC by completely stopping rendering until theme is ready
  if (loading) {
    return (
      <div className="single-food-loader" style={{ backgroundColor: 'var(--color-background, #fff)' }}>
        <style dangerouslySetInnerHTML={{
          __html: `
          body { background: #fff; margin: 0; }
        `}} />
        <div className="food-carousel"></div>
        <p>Loading theme...</p>
      </div>
    );
  }

  const cssString = Object.entries(cssVariables)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n');

  return (
    <>
      <style>{`:root {\n${cssString}\n}`}</style>
      <div className={`theme-wrapper ${templateClass}`}>
        {children}
      </div>
    </>
  );
};

// ============================================
// HOME PAGE
// ============================================
const HomePage = ({
  config,
  isFixed,
  authMethod,
  userLocation,
  onLocationUpdate,
  userToken,
  userId,
  userPhone,
  onLogout,
  onShowAuthModal,
  nearestStore,
  // ✅ Passed from sharedProps
  addresses,
  selectedAddress,
  onAddressSelect,
  onSetDefaultAddress,
  addressesLoading
}) => {
  // Map Component types to sectionActive keys
  const getSectionActiveKey = (type) => {
    const keyMap = {
      Banner: 'banner', About: 'about', SpecialMenu: 'specialMenu',
      MenuSection: 'menu', GallerySection: 'gallery', TeamSection: 'team',
      BlogSection: 'blog', ProcessSection: 'process', TestimonialSection: 'testimonials',
      MapSection: 'map', TrustBadges: 'trustBadges'
    };
    return keyMap[type];
  };

  const activeSections = config.sections.filter(section => {
    if (!config.sectionActive) return true;
    const key = getSectionActiveKey(section.type);
    if (!key) return true; // Always show unknown/unmapped sections like CategoryGrid
    return config.sectionActive[key] !== false; // Only hide if explicitly false
  });

  return (
    <>
      <Header
        isFixed={isFixed}
        logo={config.logo}
        navLinks={config.navLinks}
        restaurantName={config.name}
        basePath={config.basePath}
        userLocation={userLocation}
        onLocationUpdate={onLocationUpdate}
        userToken={userToken}
        userId={userId}
        userPhone={userPhone}
        onLogout={onLogout}
        onShowAuthModal={onShowAuthModal}
        // ✅ Add Address Props
        addresses={addresses}
        selectedAddress={selectedAddress}
        onAddressSelect={onAddressSelect}
        onSetDefaultAddress={onSetDefaultAddress}
        addressesLoading={addressesLoading}
        authMethod={authMethod}
      />
      <main>
        {activeSections.map((section, index) => {
          const Component = ComponentMap[section.type];
          if (!Component) return null;
          return (
            <Component
              key={`${section.type}-${index}`}
              config={config}
              userLocation={userLocation}
              nearestStore={nearestStore}
            />
          );
        })}
      </main>
      <Footer config={config} nearestStore={nearestStore} showNewsletter={true} />
    </>
  );
};

// Memoize HomePage to prevent redundant re-renders when used as a background
const HomePageMemo = React.memo(HomePage);

// ============================================
// STORE SELECTION PAGE (/restaurants)
// ============================================
const StoreSelectionPage = ({
  config,
  userLocation,
  onLocationUpdate,
  userToken,
  userId,
  userPhone,
  onLogout,
  onShowAuthModal,
  nearestStore,
  stores,
  branchesLoaded,
  // ✅ Passed from sharedProps
  addresses: propAddresses,
  selectedAddress: propSelectedAddress,
  onAddressSelect,
  onSetDefaultAddress,
  addressesLoading
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setStore, setPreOrder, setOrderType, setSchedule, clearSchedule } = useOrderFlow();
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);

  const [selectedStore, setSelectedStore] = useState(null);
  const [showStoreClosedModal, setShowStoreClosedModal] = useState(false);
  const [showOrderTypeSelector, setShowOrderTypeSelector] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [storeStatus, setStoreStatus] = useState(null);
  const [isPreOrderMode, setIsPreOrderMode] = useState(false);
  const [storeOutOfRange, setStoreOutOfRange] = useState(null); // stores { outOfRange: boolean, distance: number, radius: number }

  // Removed local useAddressManager - now using props for central sync
  const addresses = propAddresses;
  const selectedAddress = propSelectedAddress;
  const selectAddress = onAddressSelect;
  const setDefaultAddress = onSetDefaultAddress;

  const handleAddressSelectFromModal = async (address) => {
    console.log('📍 Address selected from LocationModal in App:', address.label);
    if (setDefaultAddress) {
      try {
        await setDefaultAddress(address.id);
      } catch (err) {
        console.error('Failed to set global default address:', err);
      }
    }
    selectAddress(address);
    window.dispatchEvent(new Event('global_address_sync'));

    // Also trigger location update to hide the modal and reset nearby stores
    if (address.latitude && address.longitude) {
      const locationData = {
        latitude: parseFloat(address.latitude),
        longitude: parseFloat(address.longitude),
        timestamp: new Date().toISOString()
      };
      handleLocationUpdateFromModal(locationData);
    }
  };

  // ✅ CRITICAL: Compute single-store check synchronously to prevent StoreSelector flash
  const isSingleStore = stores && stores.length === 1;

  const shouldAutoSelectStore = useMemo(() => {
    if (!isSingleStore) return false;
    return true; // Always auto-select single stores, distance logic handled in checkStoreAvailabilityAndProceed
  }, [isSingleStore]);

  const apiClient = useMemo(() => createApiClient(), []);

  // Check store availability and proceed
  const checkStoreAvailabilityAndProceed = useCallback(async (store, directOrderType = null) => {
    try {
      setStoreOutOfRange(null); // Reset range state
      // Always fetch fresh branch details live from API to guarantee real-time schedule detection
      let branchData;
      try {
        const response = await apiClient.get(`/web/branch/${store.id}`, { params: { _t: Date.now() } });
        if (response.data?.data) {
          branchData = response.data.data;
          apiCache.setStoreDetails(store.id, branchData);
        } else {
          branchData = apiCache.getStoreDetails(store.id) || store;
        }
      } catch (e) {
        branchData = apiCache.getStoreDetails(store.id) || store;
      }

      const storeSupportsPreOrder = branchData.allow_pre_order || branchData.pre_order_enabled || false;

      const storeWithSchedule = {
        ...store,
        week_schedule: branchData.week_schedule,
        allow_pre_order: storeSupportsPreOrder,
        pre_order_enabled: storeSupportsPreOrder,
        city: branchData.city,
        state: branchData.state,
        location: branchData.location || `${branchData.city}, ${branchData.state}`
      };

      const effectiveOutletType = config?.outletType || restaurantConfig?.outletType || 'restaurant';
      const isSweetShop = effectiveOutletType === 'sweet_shop';

      let outOfRangeData = null;
      if (!isSweetShop) {
        if (store.outOfRange !== undefined) {
          // Reuse StoreSelector's own already-computed result instead of
          // re-deriving it here from userLocation alone — StoreSelector
          // computes it against whichever location source was actually
          // active (GPS userLocation OR a manually entered pincode), so
          // re-deriving from userLocation only silently produced a wrong
          // "in range" result whenever the store was found via pincode
          // search instead of GPS.
          if (store.outOfRange) {
            outOfRangeData = {
              outOfRange: true,
              distance: store.distance,
              radius: store.delivery_radius || branchData.delivery_radius || 50
            };
          }
        } else if (userLocation?.latitude && userLocation?.longitude) {
          // Fallback for entry points that don't go through StoreSelector's
          // list (e.g. single-store auto-select), where store.outOfRange
          // was never computed in the first place.
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            parseFloat(branchData.latitude),
            parseFloat(branchData.longitude)
          );
          const radius = branchData.delivery_radius || 50;
          if (distance > radius) {
            outOfRangeData = { outOfRange: true, distance, radius };
          }
        }
      }

      // If it's a sweet shop with pre-order disabled, we default to delivery and skip OrderTypeSelector.
      // If pre-order is enabled, we show OrderTypeSelector so the user can choose Pre-Order vs Immediate.
      const finalDirectOrderType = (isSweetShop && !storeSupportsPreOrder) ? 'delivery' : directOrderType;

      setSelectedStore(storeWithSchedule);
      setStore(storeWithSchedule);

      const proceedToMenu = (type) => {
        setOrderType(type);
        clearSchedule();
        const storeSlug = storeNameToSlug(storeWithSchedule.name);
        navigate(`/restaurant/${storeSlug}`, {
          state: {
            orderDetails: {
              type: type,
              order_type: type === 'collection' ? 10 : 5,
              userLocation,
              store: storeWithSchedule,
              isPreOrder: false,
              scheduleInfo: null,
              scheduledDate: null,
              scheduledTime: null
            }
          }
        });
      };

      if (branchData.week_schedule) {
        const availability = checkStoreAvailability(branchData.week_schedule);
        const fullStatus = getStoreStatus(branchData.week_schedule);
        setStoreStatus(fullStatus);

        if (availability.isOpen) {
          setIsPreOrderMode(storeSupportsPreOrder);
          setPreOrder(storeSupportsPreOrder);
          setShowStoreClosedModal(false);

          if (finalDirectOrderType) {
            proceedToMenu(finalDirectOrderType);
          } else {
            setStoreOutOfRange(outOfRangeData);
            setShowOrderTypeSelector(true);
          }
        } else {
          // Preserve the already-computed out-of-range result even though
          // the store is closed — otherwise it's lost (reset to null at the
          // top of this function) by the time the user later clicks
          // "Schedule Order" on the closed-store modal, which opens
          // OrderTypeSelector directly without recomputing it, wrongly
          // showing Schedule Delivery as available for an out-of-range
          // customer.
          setStoreOutOfRange(outOfRangeData);
          setShowStoreClosedModal(true);
        }
      } else {
        setIsPreOrderMode(storeSupportsPreOrder);
        setPreOrder(storeSupportsPreOrder);

        if (finalDirectOrderType) {
          proceedToMenu(finalDirectOrderType);
        } else {
          setStoreOutOfRange(outOfRangeData);
          setShowOrderTypeSelector(true);
        }
      }
    } catch (error) {
      console.error('❌ Error checking store availability:', error);
      alert('Unable to verify store availability. Please try again.');
    }
  }, [apiClient, setStore, setPreOrder, config, userLocation, navigate, setOrderType, clearSchedule]);

  const handleStoreSelect = useCallback(async (store, directOrderType = null) => {
    await checkStoreAvailabilityAndProceed(store, directOrderType);
  }, [checkStoreAvailabilityAndProceed]);

  // Handle single store auto-selection & live availability re-check when arriving at /restaurants
  useEffect(() => {
    const isRestaurantsRoute = location.pathname === '/restaurants';

    if (shouldAutoSelectStore && isRestaurantsRoute && stores?.length > 0) {
      console.log('🏪 SINGLE STORE DETECTED: Auto-selecting and verifying live status...', stores[0].name);
      setIsAutoSelecting(true);
      handleStoreSelect(stores[0]).finally(() => {
        setTimeout(() => setIsAutoSelecting(false), 100);
      });
    }
  }, [stores, shouldAutoSelectStore, handleStoreSelect, location.pathname]);

  // Auto-hide Store Closed modal on Home Page as soon as merchant reopens store in backend
  useEffect(() => {
    if (!showStoreClosedModal) return;

    const targetStore = selectedStore || stores?.[0];
    if (!targetStore?.id) return;

    const checkLiveReopen = async () => {
      try {
        const response = await apiClient.get(`/web/branch/${targetStore.id}`, {
          params: { _t: Date.now() }
        });
        const freshBranch = response.data?.data;
        if (freshBranch?.week_schedule) {
          apiCache.setStoreDetails(targetStore.id, freshBranch);
          const availability = checkStoreAvailability(freshBranch.week_schedule);
          if (availability.isOpen) {
            console.log('🟢 Store has reopened! Automatically hiding Store Closed modal on Home Page.');
            setShowStoreClosedModal(false);
          }
        }
      } catch (err) {
        console.error('Error re-checking store closure on Home Page:', err);
      }
    };

    const intervalId = setInterval(checkLiveReopen, 5000);

    const onFocusOrVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkLiveReopen();
      }
    };

    window.addEventListener('focus', onFocusOrVisibility);
    document.addEventListener('visibilitychange', onFocusOrVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocusOrVisibility);
      document.removeEventListener('visibilitychange', onFocusOrVisibility);
    };
  }, [showStoreClosedModal, selectedStore, stores, apiClient]);

  const handleStoreClosedModalClose = useCallback(() => {
    setShowStoreClosedModal(false);
    setSelectedStore(null);
    setStoreStatus(null);
    setIsPreOrderMode(false);
    clearSchedule();
    if (stores && stores.length === 1) {
      navigate('/');
    }
  }, [clearSchedule, navigate, stores]);

  const handlePreOrder = useCallback(() => {
    const storeSupportsPreOrder = selectedStore?.allow_pre_order || selectedStore?.pre_order_enabled || false;
    if (!storeSupportsPreOrder) {
      alert('Sorry, this store does not support pre-orders.');
      handleStoreClosedModalClose();
      return;
    }
    setShowStoreClosedModal(false);
    setIsPreOrderMode(true);
    setPreOrder(true);
    setShowOrderTypeSelector(true);
  }, [selectedStore, handleStoreClosedModalClose, setPreOrder]);

  const handleOrderTypeContinue = useCallback((orderData) => {
    const finalIsPreOrder = orderData.isPreOrder === true;

    // Update context
    setOrderType(orderData.type);

    if (finalIsPreOrder && orderData.scheduleInfo) {
      setSchedule(orderData.scheduledDate, orderData.scheduledTime, orderData.scheduleInfo);
    } else if (finalIsPreOrder) {
      // ✅ Just enable pre-order mode, date/time will be selected later
      setPreOrder(true);
    } else {
      clearSchedule();
    }

    // Navigate to menu
    const storeSlug = storeNameToSlug(selectedStore.name);
    navigate(`/restaurant/${storeSlug}`, {
      state: {
        orderDetails: {
          type: orderData.type,
          order_type: orderData.order_type,
          userLocation: orderData.userLocation,
          store: selectedStore,
          isPreOrder: finalIsPreOrder,
          scheduleInfo: orderData.scheduleInfo || null,
          scheduledDate: orderData.scheduledDate || null,
          scheduledTime: orderData.scheduledTime || null
        }
      }
    });
  }, [selectedStore, navigate, setOrderType, setSchedule, clearSchedule]);

  const handleCloseStoreSelector = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleCloseOrderTypeSelector = useCallback(() => {
    setShowOrderTypeSelector(false);
    setSelectedStore(null);
    setIsPreOrderMode(false);
    clearSchedule();
    if (stores && stores.length === 1) {
      navigate('/');
    }
  }, [clearSchedule, navigate, stores]);

  const handleRequestLocation = useCallback(() => {
    setShowLocationModal(true);
  }, []);

  const handleLocationUpdateFromModal = useCallback((newLocation) => {
    if (onLocationUpdate) {
      onLocationUpdate(newLocation);
    }
    setShowLocationModal(false);
  }, [onLocationUpdate]);

  const storeSupportsPreOrder = selectedStore?.allow_pre_order || selectedStore?.pre_order_enabled || false;

  return (
    <>
      {/* Home page as background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden' }}>
        <HomePageMemo
          config={config}
          isFixed={false}
          userLocation={userLocation}
          onLocationUpdate={onLocationUpdate}
          userToken={userToken}
          userId={userId}
          userPhone={userPhone}
          onLogout={onLogout}
          onShowAuthModal={onShowAuthModal}
          nearestStore={nearestStore}
          // ✅ Pass address props
          addresses={addresses}
          selectedAddress={selectedAddress}
          onAddressSelect={onAddressSelect}
          onSetDefaultAddress={onSetDefaultAddress}
          addressesLoading={addressesLoading}
        />
      </div>

      {/* ✅ Branches haven't resolved yet — don't render StoreSelector or the
          auto-select decision until we actually know the branch count, so a
          single-branch tenant never sees the popup flash while stores===[]
          just means "not loaded yet" rather than "confirmed multi-store". */}
      {!branchesLoaded && !showOrderTypeSelector && !showStoreClosedModal && (
        <div className="auto-select-overlay">
          <div className="auto-select-card">
            <div className="auto-select-spinner">
              <div className="auto-select-spinner-ring"></div>
              <div className="auto-select-spinner-ring auto-select-spinner-ring--inner"></div>
              <div className="auto-select-spinner-dot"></div>
            </div>
            <p className="auto-select-text">Loading stores…</p>
            <div className="auto-select-progress">
              <div className="auto-select-progress-bar"></div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Smooth loading state for single-store auto-selection */}
      {shouldAutoSelectStore && isAutoSelecting && !showOrderTypeSelector && !showStoreClosedModal && (
        <div className="auto-select-overlay">
          <div className="auto-select-card">
            <div className="auto-select-spinner">
              <div className="auto-select-spinner-ring"></div>
              <div className="auto-select-spinner-ring auto-select-spinner-ring--inner"></div>
              <div className="auto-select-spinner-dot"></div>
            </div>
            <p className="auto-select-text">Preparing Menu</p>
            <div className="auto-select-progress">
              <div className="auto-select-progress-bar"></div>
            </div>
          </div>
        </div>
      )}

      {/* Store Selector - only show for multi-store scenarios or when single store is out of radius */}
      {branchesLoaded && !shouldAutoSelectStore && !showOrderTypeSelector && !showStoreClosedModal && !hasAutoSelected && (
        <StoreSelector
          isOpen={true}
          isHidden={showLocationModal}
          onClose={handleCloseStoreSelector}
          onSelectStore={handleStoreSelect}
          userLocation={userLocation}
          onRequestLocation={handleRequestLocation}
          outletType={config?.outletType || restaurantConfig?.outletType || 'restaurant'} // Use dynamic config with fallback
        />
      )}

      {/* Store Closed Modal */}
      {showStoreClosedModal && selectedStore && storeStatus && (
        <StoreClosedModal
          isOpen={showStoreClosedModal}
          onClose={handleStoreClosedModalClose}
          onPreOrder={handlePreOrder}
          store={selectedStore}
          storeStatus={storeStatus}
          supportsPreOrder={storeSupportsPreOrder}
          hasOtherStores={!isSingleStore}
        />
      )}

      {/* Order Type Selector */}
      {showOrderTypeSelector && selectedStore && (
        <OrderTypeSelector
          key={`${selectedStore.id}-${isPreOrderMode}`}
          isOpen={true}
          onClose={handleCloseOrderTypeSelector}
          selectedStore={selectedStore}
          onContinue={handleOrderTypeContinue}
          userLocation={userLocation}
          isPreOrder={isPreOrderMode}
          outOfRangeData={storeOutOfRange}
        />
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <LocationModal
          isOpen={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onLocationUpdate={handleLocationUpdateFromModal}
          currentLocation={localStorage.getItem('location_display') || 'Detect Location'}
          addresses={addresses}
          userId={userId}
          selectedAddress={selectedAddress}
          onAddressSelect={handleAddressSelectFromModal}
        />
      )}
    </>
  );
};

// ============================================
// RESTAURANT MENU PAGE (/restaurant/:storeSlug)
// ============================================
const RestaurantMenuPage = ({
  config,
  authMethod,
  userLocation,
  onLocationUpdate,
  userToken,
  userId,
  userPhone,
  stores,
  branchesLoaded,
  onLogout,
  onAuthUpdate,
  onShowAuthModal,
  nearestStore,
  // ✅ Passed from sharedProps
  addresses,
  selectedAddress,
  onAddressSelect,
  onSetDefaultAddress,
  onCreateAddress,
  onUpdateAddress,
  onDeleteAddress,
  addressesLoading
}) => {
  const { storeSlug, categorySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStore: contextStore, setStore, orderType, isPreOrder, scheduledDate, scheduledTime, setOrderType, setSchedule, clearSchedule, setPreOrder, scheduleInfo: contextScheduleInfo } = useOrderFlow();

  const [selectedStore, setSelectedStore] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve store from slug on mount
  useEffect(() => {
    const resolveStore = async () => {
      setIsLoading(true);

      // Check if we have order details from navigation state (and it must have a valid store object)
      if (location.state?.orderDetails?.store) {
        const od = location.state.orderDetails;
        setSelectedStore(od.store);
        setOrderDetails(od);
        setIsLoading(false);
        return;
      }

      // Check if context has the store
      if (contextStore && storeNameToSlug(contextStore.name) === storeSlug) {
        setSelectedStore(contextStore);
        setOrderDetails({
          type: orderType || 'delivery',
          order_type: orderType === 'collection' ? 10 : 5,
          store: contextStore,
          isPreOrder: isPreOrder,
          scheduledDate: scheduledDate,
          scheduledTime: scheduledTime,
          scheduleInfo: contextScheduleInfo
        });
        setIsLoading(false);
        return;
      }

      // Check sessionStorage for cached store details (survives page reloads from payment gateways).
      // Also require tenant_id to match the currently resolved tenant — this
      // is a plain browser-origin-scoped cache with no natural tenant
      // isolation, so a stale entry from a different tenant's storefront
      // (same browser session) must never be trusted just because its name
      // slug happens to also match the current URL.
      try {
        const storedDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
        if (
          storedDetails?.id &&
          storedDetails?.name &&
          storeNameToSlug(storedDetails.name) === storeSlug &&
          String(storedDetails?.tenant_id) === String(getTenantId())
        ) {
          setSelectedStore(storedDetails);
          setStore(storedDetails);
          setOrderDetails({
            type: orderType || 'delivery',
            order_type: orderType === 'collection' ? 10 : 5,
            store: storedDetails,
            isPreOrder: isPreOrder,
            scheduledDate: scheduledDate,
            scheduledTime: scheduledTime,
            scheduleInfo: contextScheduleInfo
          });
          setIsLoading(false);
          return;
        }
      } catch (e) {
        // ignore
      }

      // Fetch store by slug (fallback)
      const store = await fetchStoreByNameSlug(storeSlug);
      if (store) {
        setSelectedStore(store);
        setStore(store);
        setOrderDetails({
          type: orderType || 'delivery',
          order_type: orderType === 'collection' ? 10 : 5,
          store: store,
          isPreOrder: isPreOrder,
          scheduledDate: scheduledDate,
          scheduledTime: scheduledTime,
          scheduleInfo: contextScheduleInfo
        });
      } else {
        console.error('❌ Store not found for slug:', storeSlug);
        navigate('/restaurants', { replace: true });
        return;
      }
      setIsLoading(false);
    };

    resolveStore();
  }, [storeSlug]);

  const handleCloseMenu = useCallback(() => {
    navigate('/restaurants');
  }, [navigate]);

  const handleLocationChangeFromMenu = useCallback(() => {
    navigate('/restaurants');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="single-food-loader">
        <div className="food-carousel"></div>
        <p>Loading menu…</p>
      </div>
    );
  }

  if (!selectedStore || !orderDetails) {
    if (isLoading) return (
      <div className="single-food-loader">
        <div className="food-carousel"></div>
        <p>Preparing menu...</p>
      </div>
    );
    return (
      <div className="single-food-loader">
        <p>Redirecting to store selection...</p>
      </div>
    );
  }

  // ✅ Gap A fix: re-check live store availability at this single, centralized
  // /restaurant/:slug resolution point — covers every way of arriving here
  // (Header cart-icon click from Home Page, direct/bookmarked URL, browser
  // back/forward, sessionStorage restore), not just one entry point. Reuses
  // the existing checkStoreAvailability + StoreClosedModal, no new logic.
  // When pre-order IS supported, we deliberately let the user through
  // unchanged — RestaurantMenuApp/CartContent already handle "closed but
  // pre-order enabled" as a legitimate Schedule Order flow.
  const gapAAvailability = selectedStore.week_schedule
    ? checkStoreAvailability(selectedStore.week_schedule)
    : { isOpen: true, message: '', nextOpenTime: null };
  const gapAStoreSupportsPreOrder = selectedStore?.allow_pre_order === true || selectedStore?.pre_order_enabled === true;

  if (!gapAAvailability.isOpen && !gapAStoreSupportsPreOrder) {
    return (
      <>
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden' }}>
          <HomePageMemo
            config={config}
            isFixed={false}
            userLocation={userLocation}
            onLocationUpdate={onLocationUpdate}
            userToken={userToken}
            userId={userId}
            userPhone={userPhone}
            onLogout={onLogout}
            onShowAuthModal={onShowAuthModal}
            nearestStore={nearestStore}
            addresses={addresses}
            selectedAddress={selectedAddress}
            onAddressSelect={onAddressSelect}
            onSetDefaultAddress={onSetDefaultAddress}
            addressesLoading={addressesLoading}
          />
        </div>
        <StoreClosedModal
          isOpen={true}
          onClose={() => navigate('/')}
          onPreOrder={() => {}}
          store={selectedStore}
          storeStatus={{ message: gapAAvailability.message, nextOpenTime: gapAAvailability.nextOpenTime }}
          supportsPreOrder={false}
          hasOtherStores={!branchesLoaded || stores?.length !== 1}
        />
      </>
    );
  }

  return (
    <RestaurantMenuApp
      key={`${selectedStore.id}-${orderDetails.type}-${orderDetails.isPreOrder}-${userId || 'guest'}`}
      isOpen={true}
      onClose={handleCloseMenu}
      selectedStore={selectedStore}
      orderDetails={orderDetails}
      initialCategorySlug={categorySlug}
      outletType={config?.outletType || restaurantConfig?.outletType || 'restaurant'}
      menuFilterSettings={config?.menuFilterSettings || restaurantConfig?.menuFilterSettings}
      templateType={config?.templateType || restaurantConfig?.templateType || 'template1'}
      authMethod={authMethod}
      HeaderComponent={Header}
      headerProps={{
        isFixed: true,
        logo: config.logo,
        navLinks: config.navLinks,
        restaurantName: config.name,
        userLocation: userLocation,
        userToken: userToken,
        userId: userId,
        userPhone: userPhone,
        onLogout: onLogout,
        onShowAuthModal: onShowAuthModal,
        basePath: config.basePath,
        // ✅ Add address props for Header inside MenuApp
        addresses: addresses,
        selectedAddress: selectedAddress,
        onAddressSelect: onAddressSelect,
        onSetDefaultAddress: onSetDefaultAddress,
        addressesLoading: addressesLoading,
        authMethod: authMethod
      }}
      onLocationChange={handleLocationChangeFromMenu}
      onAuthUpdate={onAuthUpdate}
      selectedAddress={selectedAddress}
      addresses={addresses}
      onAddressSelect={onAddressSelect}
      onSetDefaultAddress={onSetDefaultAddress}
      onCreateAddress={onCreateAddress}
      onUpdateAddress={onUpdateAddress}
      onDeleteAddress={onDeleteAddress}
      addressesLoading={addressesLoading}
    />
  );
};

// ============================================
// PROFILE SECTION PAGES
// ============================================
const ProfileSectionPage = ({
  config,
  userLocation,
  onLocationUpdate,
  userToken,
  userId,
  userPhone,
  onLogout,
  onShowAuthModal,
  nearestStore,
  SectionComponent,
  authMethod,
  // ✅ Passed from sharedProps
  addresses,
  selectedAddress,
  onAddressSelect,
  onSetDefaultAddress,
  addressesLoading
}) => {
  const navigate = useNavigate();

  // If not logged in, redirect to home
  useEffect(() => {
    if (!userToken) {
      navigate('/');
    }
  }, [userToken, navigate]);

  if (!userToken) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Header
        isFixed={true}
        logo={config.logo}
        navLinks={config.navLinks}
        restaurantName={config.name}
        basePath={config.basePath}
        userLocation={userLocation}
        onLocationUpdate={onLocationUpdate}
        userToken={userToken}
        userId={userId}
        userPhone={userPhone}
        onLogout={onLogout}
        onShowAuthModal={onShowAuthModal}
        // ✅ Add address props for Header in Profile Pages
        addresses={addresses}
        selectedAddress={selectedAddress}
        onAddressSelect={onAddressSelect}
        onSetDefaultAddress={onSetDefaultAddress}
        addressesLoading={addressesLoading}
        authMethod={authMethod}
      />

      {/* Container for profile content under the header */}
      <main style={{ flex: 1, position: 'relative', paddingTop: '70px' }}>
        <SectionComponent isOpen={true} onClose={() => navigate('/')} userId={userId} />
      </main>

      <Footer config={config} nearestStore={nearestStore} showNewsletter={false} />
    </div>
  );
};

// ========================================
// Scroll to Top Component
// ========================================

// Remembers each history entry's scroll position (keyed by React Router's
// per-entry location.key) so browser Back/Forward can restore it. Module
// level so it survives this component's re-renders across the whole session.
const scrollPositionsByKey = new Map();

const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // POP = browser Back/Forward: restore the position that history entry
    // had when the user left it (0 if never visited before, e.g. a fresh
    // session). PUSH/REPLACE = a real new-page navigation (clicked a link,
    // called navigate()): keep the existing behavior of starting at top.
    const targetY = navigationType === 'POP' ? (scrollPositionsByKey.get(key) || 0) : 0;

    const scrollToTarget = () => {
      // The options-object form with an explicit behavior is required: this
      // app sets a global "scroll-behavior: smooth" on html/body (App.css,
      // index.css), and the legacy 2-argument scrollTo(x, y) form respects
      // that CSS setting — so restoring a saved position visibly animated
      // up/down to it instead of landing there directly. Passing
      // behavior:'instant' explicitly overrides the CSS per spec, making
      // every restore (and the "start at top" case) an instant jump.
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: targetY, left: 0, behavior: 'instant' });
      document.body.scrollTo({ top: targetY, left: 0, behavior: 'instant' });
    };

    scrollToTarget();

    if (targetY === 0) {
      // Original behavior preserved exactly: one backup call is enough
      // when the target is simply "top".
      const timer = setTimeout(scrollToTarget, 0);
      return () => clearTimeout(timer);
    }

    // Restoring to a remembered (non-zero) position: the destination page
    // (e.g. Home) can still be growing after this runs. Confirmed by direct
    // tracing: this tenant's Home renders in two waves — a partial render
    // first, then a fuller one ~2-3s later once a slower API call resolves
    // — and either wave can still be in progress when this effect runs.
    // Rather than guess an interval, react to the DOM actually changing:
    // this app's body is a fixed height:100% box with overflow-y:auto (see
    // App.css), not an auto-height box, so a ResizeObserver on body would
    // never fire when its *content* grows — only scrollHeight changes, not
    // body's own box size. A MutationObserver on the subtree does fire for
    // exactly that, so it's used here to re-apply the saved position every
    // time content changes.
    //
    // Deliberately does NOT stop early just because the target was
    // momentarily reached: an earlier version did, and that's exactly what
    // made restoration intermittent — the second content wave would land
    // after the observer had already given up, and nothing corrected the
    // resulting drift. Instead this keeps re-asserting the saved position
    // as the last word for the whole window below, only stopping via the
    // safety timer once the page has had a generous chance to fully settle.
    // If the user scrolls (wheel/touch) during this window, that's a
    // deliberate action and takes priority — stop re-asserting the saved
    // position so late-arriving content never yanks them away from
    // somewhere they just chose to scroll to themselves. The listeners are
    // attached after a short delay rather than immediately: a wheel/touch
    // interaction that was already in flight right as this navigation
    // landed (e.g. residual momentum from the scroll that revealed the
    // link the user just clicked) isn't new input about *this* page and
    // shouldn't be read as the user overriding a restore they haven't
    // even seen yet.
    let userTookOver = false;
    let listenersAttached = false;
    const stopOnUserInput = () => { userTookOver = true; cleanup(); };
    const attachTimer = setTimeout(() => {
      listenersAttached = true;
      window.addEventListener('wheel', stopOnUserInput, { passive: true, once: true });
      window.addEventListener('touchmove', stopOnUserInput, { passive: true, once: true });
    }, 400);

    let debounceTimer = null;
    const attempt = () => { if (!userTookOver) scrollToTarget(); };

    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(attempt, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    const safetyTimer = setTimeout(() => { observer.disconnect(); }, 8000);

    function cleanup() {
      clearTimeout(debounceTimer);
      clearTimeout(safetyTimer);
      clearTimeout(attachTimer);
      observer.disconnect();
      if (listenersAttached) {
        window.removeEventListener('wheel', stopOnUserInput);
        window.removeEventListener('touchmove', stopOnUserInput);
      }
    }

    return cleanup;
  }, [pathname, key, navigationType]);

  // Continuously remember the current history entry's scroll position so
  // it's available if the user navigates away and later comes back to it.
  // This app's global CSS (App.css: "html, body { height: 100% }" plus
  // body's own overflow-y: auto) makes <body> the element that actually
  // scrolls on most pages rather than the window/documentElement, so the
  // listener must be attached with capture:true on document — scroll
  // events don't bubble, but capture-phase listeners still see them fire
  // on any descendant, including body. Reading the max of all three
  // possible scroll positions covers whichever one is truly active on a
  // given page, mirroring the existing defensive multi-target scroll calls
  // above.
  //
  // useLayoutEffect, not useEffect, is required here: React swaps the DOM
  // to the new route synchronously (before paint), but a plain useEffect's
  // cleanup/setup is deferred until after paint. That gap let the *old*
  // page's listener stay attached while the *new* page's DOM (and its
  // smooth-scroll-to-top animation, from App.css's global
  // "scroll-behavior: smooth") was already live — so scroll events from
  // the new page's transition were captured under the old page's key,
  // silently overwriting its correct saved position with a mid-animation
  // value. This is what made restoration intermittent. useLayoutEffect's
  // synchronous cleanup/setup closes that gap: the listener is swapped to
  // the new key in the same commit as the DOM swap, before any frame of
  // the transition animation can fire.
  useLayoutEffect(() => {
    const handleScroll = () => {
      const y = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
        document.body.scrollTop
      );
      scrollPositionsByKey.set(key, y);
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [key]);

  return null;
};

// ========================================
// Global Toast Component
// ========================================
const GlobalToast = ({ toast }) => {
  if (!toast) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: '24px',
        zIndex: 99999,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 24px',
        borderRadius: '16px',
        fontSize: '15px',
        fontWeight: '600',
        boxShadow: toast.type === 'success'
          ? '0 10px 40px -10px rgba(76, 175, 80, 0.4)'
          : '0 10px 40px -10px rgba(220, 53, 69, 0.4)',
        animation: 'userProfileToastSlide 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
        minWidth: '260px',
        maxWidth: '380px',
        background: toast.type === 'success'
          ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.95), rgba(76, 175, 80, 0.9))'
          : 'linear-gradient(135deg, rgba(198, 40, 40, 0.95), rgba(220, 53, 69, 0.9))',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(12px)',
        transformOrigin: 'top right'
      }}
    >
      <span style={{
        fontFamily: 'var(--font-primary, "Inter", Arial, sans-serif)',
        letterSpacing: '0.3px',
        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }}>
        {toast.message}
      </span>
    </div>,
    document.body
  );
};

// ========================================
// Main App Content Component
// ========================================
const AppContent = () => {
  const [config, setConfig] = useState(null);
  const [isFixed, setIsFixed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [userToken, setUserToken] = useState(() => getValidAuthSession()?.token || null);
  const [userId, setUserId] = useState(() => getValidAuthSession()?.userId || null);
  const [userPhone, setUserPhone] = useState(() => getValidAuthSession()?.phone || null);
  const [stores, setStores] = useState([]);
  // ✅ Tracks whether fetchBranches() has resolved at least once, so
  // StoreSelectionPage can tell "0 stores because still loading" apart
  // from "0/1/many stores, confirmed" — see fetchBranches() below.
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const [nearestStore, setNearestStore] = useState(null);
  const [globalToast, setGlobalToast] = useState(null);
  const [authMethod, setAuthMethod] = useState('email_password'); // 'email_password' | 'phone_otp'
  const deliveryRadius = 50;

  // Auto-dismiss global toast after 3 seconds
  useEffect(() => {
    if (globalToast) {
      const timer = setTimeout(() => setGlobalToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [globalToast]);

  const navigate = useNavigate();
  const location = useLocation();

  const [apiClient, setApiClient] = useState(() => createApiClient());

  const {
    addresses,
    selectedAddress,
    selectAddress,
    setDefaultAddress,
    createAddress,
    updateAddress,
    deleteAddress,
    loading: addressesLoading
  } = useAddressManager(null, userId);

  // Initialize branding and config
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        await brandingService.fetchInitData();
        const client = createApiClient();
        setApiClient(() => client);
        await brandingService.applyCompleteBranding();
        const brandedConfig = await getRestaurantConfig();
        setConfig(brandedConfig);

        // Fetch site settings for auth method
        try {
          const settingsRes = await client.get('/web/settings');
          const settingsData = settingsRes.data;
          // settings may be at top-level or nested under .data
          const resolvedSettings = settingsData?.data || settingsData;

          if (resolvedSettings?.site_customer_auth_method) {
            setAuthMethod(resolvedSettings.site_customer_auth_method);
            console.log('🔑 Auth method:', resolvedSettings.site_customer_auth_method);
          }
          const apiOutletType = resolvedSettings?.outlet_type;
          console.log('🏪 Settings API response outlet_type:', apiOutletType, '| raw settingsData keys:', Object.keys(settingsData || {}));
          if (apiOutletType) {
            console.log('✅ Outlet Type resolved from settings API:', apiOutletType);
            setConfig(prev => ({ ...prev, outletType: apiOutletType }));
          }
        } catch (settingsErr) {
          console.error('⚠️ Could not fetch settings, defaulting to email_password:', settingsErr.message);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error initializing app:', error);
        setConfig(restaurantConfig);
        setIsLoading(false);
      }
    };
    initializeApp();
  }, []);

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      // Check cache first
      const cachedBranches = apiCache.getBranches();
      if (cachedBranches) {
        // ✅ Allow all branches, even those without coordinates
        const validStores = cachedBranches;
        setStores(validStores);
        return validStores;
      }

      const response = await apiClient.get('/web/branch');
      if (response.data?.data) {
        // ✅ Allow all branches, even those without coordinates
        const validStores = response.data.data;
        apiCache.setBranches(response.data.data);
        setStores(validStores);
        return validStores;
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error.message);
      return [];
    } finally {
      setBranchesLoaded(true);
    }
  }, [apiClient]);

  // Location update handler
  const handleLocationUpdate = useCallback((newLocation) => {
    setUserLocation(newLocation);
    if (newLocation) {
      localStorage.setItem('user_location', JSON.stringify(newLocation));
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user_location',
        newValue: JSON.stringify(newLocation)
      }));
    }
  }, []);

  // Auth handlers
  const handleAuthUpdate = useCallback((token, id, phone) => {
    setUserToken(token);
    setUserId(id);
    setUserPhone(phone);
  }, []);

  const handleAuthSuccess = useCallback((token, id, phone) => {
    handleAuthUpdate(token, id, phone);
    setGlobalToast({ type: 'success', message: 'Logged In Successfully!' });
  }, [handleAuthUpdate]);

  // Some homepage sections (e.g. Template 16's "Our Specialties") render
  // their own local AuthModal instance for an add-to-cart login prompt,
  // rather than this component's own flow. That modal successfully signs
  // the user in (the session cookie is set), but has no way to update this
  // component's userToken/userId state directly, so the Header kept
  // showing "Login" afterward. Re-sync from the same session storage
  // whenever such a login completes elsewhere.
  useEffect(() => {
    const onAuthUpdated = () => {
      const session = getValidAuthSession();
      if (session?.token) {
        handleAuthUpdate(session.token, session.userId, session.phone);
      }
    };
    window.addEventListener('auth_updated', onAuthUpdated);
    return () => window.removeEventListener('auth_updated', onAuthUpdated);
  }, [handleAuthUpdate]);

  const handleLogout = useCallback(async () => {
    // Call server-side logout
    const token = getValidAuthSession()?.token;
    if (token) {
      try {
        await apiClient.post('/auth/logout', {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('⚠️ Server logout failed:', err.message);
      }
    }

    clearAuthSession();

    setUserToken(null);
    setUserId(null);
    setUserPhone(null);

    // Clear cart from localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('resto_cart_data')) {
          localStorage.removeItem(key);
        }
      });
      // ✅ Notify Header to refresh cart badge (clears count to 0)
      window.dispatchEvent(new Event('cart_updated'));
    } catch (error) {
      console.error('❌ Error clearing cart:', error);
    }

    apiCache.invalidateAll();
    setGlobalToast({ type: 'error', message: 'Logged Out Successfully.' });
    navigate('/');
  }, [navigate, apiClient]);

  // Effect: Storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user_location' && e.newValue) {
        try {
          setUserLocation(JSON.parse(e.newValue));
        } catch (error) {
          console.error('Error parsing location:', error);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Effect: Load saved location
  useEffect(() => {
    const savedLocation = localStorage.getItem('user_location');
    if (savedLocation) {
      try {
        setUserLocation(JSON.parse(savedLocation));
      } catch (e) {
        console.error('Error parsing saved location:', e);
      }
    }
  }, []);

  // Effect: Restore auth — re-validated against the currently active tenant
  // (tenant id may not have been resolved yet at the initial useState() calls
  // above, since brandingService.fetchInitData() is async).
  useEffect(() => {
    const session = getValidAuthSession();
    if (session?.token && session?.userId) {
      setUserToken(session.token);
      setUserId(session.userId);
      setUserPhone(session.phone);
    } else if (!session) {
      // Stale/mismatched session was cleared — make sure in-memory state agrees.
      setUserToken(null);
      setUserId(null);
      setUserPhone(null);
    }
  }, []);

  // Effect: Calculate nearest store
  useEffect(() => {
    const calculateNearest = async () => {
      if (stores.length === 0) return;

      // Rule 1: If there is a single store, directly show it (no radius check, location ignores)
      if (stores.length === 1) {
        const singleStore = stores[0];
        if (!singleStore.week_schedule) {
          try {
            const res = await apiClient.get(`/web/branch/${singleStore.id}`);
            if (res.data?.data) {
              setNearestStore(res.data.data);
              return;
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch schedule for single store:', err.message);
          }
        }
        setNearestStore(singleStore);
        return;
      }

      // Rule 3: If user location is enabled, only show the nearest branch by radius
      if (userLocation?.latitude) {
        const storesWithDistance = stores.map(store => ({
          ...store,
          distance: calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            parseFloat(store.latitude),
            parseFloat(store.longitude)
          )
        }));

        const storesInRadius = storesWithDistance.filter(store => {
          const effectiveOutletType = config?.outletType || restaurantConfig?.outletType || 'restaurant';
          if (effectiveOutletType === 'sweet_shop') return true;
          const radius = store.delivery_radius || deliveryRadius;
          return store.distance && store.distance <= radius;
        });

        if (storesInRadius.length > 0) {
          const nearest = storesInRadius.sort((a, b) => a.distance - b.distance)[0];

          if (!nearest.week_schedule) {
            try {
              const res = await apiClient.get(`/web/branch/${nearest.id}`);
              if (res.data?.data) {
                setNearestStore({ ...nearest, week_schedule: res.data.data.week_schedule });
                return;
              }
            } catch (err) {
              console.warn('⚠️ Could not fetch schedule for nearest store:', err.message);
            }
          }
          setNearestStore(nearest);
          return;
        }
        // If location is ON but no stores are found in radius, we still fall back to the first branch 
        // to avoid an empty footer (implied Rule 2).
      }

      // Rule 2: If user location is NOT enabled (or none in radius), show 1st branch details
      const firstBranch = stores[0];
      if (!firstBranch.week_schedule) {
        try {
          const res = await apiClient.get(`/web/branch/${firstBranch.id}`);
          if (res.data?.data) {
            setNearestStore(res.data.data);
            return;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch schedule for first branch fallback:', err.message);
        }
      }
      setNearestStore(firstBranch);
    };

    calculateNearest();
  }, [userLocation, stores, deliveryRadius, apiClient, config?.outletType]);

  // Effect: Fetch branches
  useEffect(() => {
    if (!isLoading) {
      fetchBranches();
    }
  }, [fetchBranches, isLoading]);

  // Effect: Request initial location
  useEffect(() => {
    const requestInitialLocation = () => {
      const savedLocation = localStorage.getItem('user_location');

      if (savedLocation) {
        try {
          const parsed = JSON.parse(savedLocation);
          if (parsed &&
            typeof parsed.latitude === 'number' &&
            typeof parsed.longitude === 'number' &&
            !isNaN(parsed.latitude) &&
            !isNaN(parsed.longitude)) {
            setUserLocation(parsed);
            return;
          } else {
            localStorage.removeItem('user_location');
          }
        } catch (e) {
          localStorage.removeItem('user_location');
        }
      }
      // 🛑 Removed: No longer automatically requesting location on page load.
      // Users will only be asked to share their location when they proactively click 
      // "Detect Location" inside the LocationModal.
      setUserLocation(null);
    };
    requestInitialLocation();
  }, [handleLocationUpdate]);

  // Effect: Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      setIsFixed(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    // Payment/success routes don't need full app initialization - render them immediately
    // This prevents showing the food emoji loader when returning from payment gateways
    const currentPath = location.pathname;
    if (currentPath === '/payment/success' || currentPath === '/payment/cancel' || currentPath === '/order/cash-success') {
      const isCancel = currentPath === '/payment/cancel';
      const isCash = currentPath === '/order/cash-success';
      const statusText = isCancel ? 'Processing payment status…' : (isCash ? 'Verifying order…' : 'Processing payment…');

      return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f7faf9', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          <div style={{ width: '42px', height: '42px', border: '3px solid #e5e7eb', borderTop: '3px solid #16a34a', borderRadius: '50%', animation: 'spin-fast 1s linear infinite' }}></div>
          <p style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280' }}>{statusText}</p>
          <style>{`@keyframes spin-fast { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    return (
      <div className="single-food-loader">
        <div className="food-carousel"></div>
        <p>Loading…</p>
      </div>
    );
  }

  if (!config) {
    return <div>Error: Unable to load restaurant configuration</div>;
  }

  // Filter nav links based on section visibility
  const activeNavLinks = (config.navLinks || []).filter(link => {
    if (!config.sectionActive) return true;
    const typeMap = {
      '#about': 'about', '#special-menu': 'specialMenu', '#menu': 'menu',
      '#gallery': 'gallery', '#our_team': 'team', '#blog': 'blog', '#map': 'map'
    };
    const key = typeMap[link.href];
    if (!key) return true;
    return config.sectionActive[key] !== false;
  });

  // Shared props for all pages
  const sharedProps = {
    config: {
      ...config,
      navLinks: activeNavLinks
    },
    authMethod,
    userLocation,
    onLocationUpdate: handleLocationUpdate,
    userToken,
    userId,
    userPhone,
    onLogout: handleLogout,
    onShowAuthModal: () => navigate('/login', { state: { from: location.pathname } }),
    nearestStore,
    stores,
    branchesLoaded,
    addresses,                   // ✅ Shared address state
    selectedAddress,             // ✅ Shared address state
    onAddressSelect: selectAddress,
    onSetDefaultAddress: setDefaultAddress,
    onCreateAddress: createAddress,
    onUpdateAddress: updateAddress,
    onDeleteAddress: deleteAddress,
    addressesLoading: addressesLoading
  };

  return (
    <ThemeProvider
      template={config.templateType}
      backgroundImages={config.backgroundImages}
      customTheme={config.theme}
    >
      <div className="font-sans">
        <ScrollToTop />
        <GDPRBanner config={config} existingLocation={userLocation} />
        <GlobalToast toast={globalToast} />

        <Routes>
          {/* Auth Routes */}
          <Route
            path="/login"
            element={<AuthPage config={config} authMethod={authMethod} onAuthSuccess={handleAuthSuccess} userToken={userToken} />}
          />
          <Route
            path="/signup"
            element={<AuthPage config={config} authMethod={authMethod} onAuthSuccess={handleAuthSuccess} userToken={userToken} />}
          />
          <Route
            path="/forgot-password"
            element={<AuthPage config={config} authMethod={authMethod} onAuthSuccess={handleAuthSuccess} userToken={userToken} />}
          />
          {/* Home Page */}
          <Route
            path="/"
            element={
              <HomePage
                {...sharedProps}
                isFixed={isFixed}
              />
            }
          />

          {/* Store Selection */}
          <Route
            path="/restaurants"
            element={
              <StoreSelectionPage {...sharedProps} />
            }
          />

          {/* Legacy /order route - redirect to /restaurants */}
          <Route
            path="/order"
            element={<LegacyOrderRedirect />}
          />

          {/* Restaurant Menu — :categorySlug is optional so the same route
              (and the same RestaurantMenuApp instance) serves both
              /restaurant/:storeSlug and /restaurant/:storeSlug/:categorySlug,
              avoiding any remount when only the category segment changes. */}
          <Route
            path="/restaurant/:storeSlug/:categorySlug?"
            element={
              <RestaurantMenuPage
                {...sharedProps}
                onAuthUpdate={handleAuthUpdate}
              />
            }
          />

          {/* Policy Pages */}
          {config.footer?.policies?.map((policy, index) => (
            <Route
              key={index}
              path={policy.path}
              element={<PolicyPage {...sharedProps} isFixed={isFixed} />}
            />
          ))}

          {/* Post-Order Flows */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/result" element={<PaymentResult />} />
          <Route path="/order/cash-success" element={<CashOrderSuccess />} />

          {/* Profile Section Pages */}
          <Route path="/orders" element={<ProfileSectionPage {...sharedProps} SectionComponent={MyOrdersModal} />} />
          <Route path="/orders/:orderId" element={<ProfileSectionPage {...sharedProps} SectionComponent={MyOrdersModal} />} />
          <Route path="/order/:orderId" element={<ProfileSectionPage {...sharedProps} SectionComponent={MyOrdersModal} />} />
          <Route path="/transactions" element={<ProfileSectionPage {...sharedProps} SectionComponent={MyTransactionsModal} />} />
          <Route path="/addresses" element={<ProfileSectionPage {...sharedProps} SectionComponent={ManageAddressesModal} />} />
          <Route path="/loyalty" element={<ProfileSectionPage {...sharedProps} SectionComponent={LoyaltyModal} />} />
          <Route path="/preferences" element={<ProfileSectionPage {...sharedProps} SectionComponent={PreferencesModal} />} />

          {/* Catch-all */}
          <Route
            path="*"
            element={
              <HomePage
                {...sharedProps}
                isFixed={isFixed}
              />
            }
          />
        </Routes>
      </div>
    </ThemeProvider>
  );
};

// ========================================
// Legacy /order redirect
// ========================================
const LegacyOrderRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/restaurants', { replace: true });
  }, [navigate]);
  return null;
};

// ========================================
// Main App Component
// ========================================
const App = () => {
  return (
    <Router>
      <OrderFlowProvider>
        <AppContent />
      </OrderFlowProvider>
    </Router>
  );
};

export default App;
